import { ButtonProps } from '@amzn/awsui-components-core/button';
import { SelectProps } from '@amzn/awsui-components-core/select';
import { TreeViewProps } from '@amzn/awsui-components-core/tree-view';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVersionDateFilter } from 'src/components/star-grid/hooks/useVersionDateFilter';
import { useVersionHistoryPreview } from 'src/components/star-grid/hooks/useVersionHistoryPreview';
import { useStarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { VersionChoices, VersionTreeItem } from 'src/components/star-grid/reportSelectors.types';
import { buildVersionCatalog, expandedVersionGroups } from 'src/components/star-grid/utils/versionCatalog';

const useVersionDisclosure = (disabled: boolean | undefined) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef<ButtonProps.Ref>(null);
  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);
  const close = useCallback(() => setIsOpen(false), []);
  const dismiss = useCallback(() => {
    close();
    triggerRef.current?.focus();
  }, [close]);
  const toggle = (): void => {
    setQuery('');
    setIsOpen(!isOpen);
  };
  return { isOpen, query, setQuery, triggerRef, close, dismiss, toggle };
};

const useVersionExpansion = (items: readonly VersionTreeItem[], selected: string | undefined, query: string, isOpen: boolean) => {
  const [expanded, setExpanded] = useState<readonly string[]>([]);
  const defaultExpanded = useMemo(() => {
    const firstGroups = [items[0]?.id, items[0]?.children?.[0]?.id].filter((id): id is string => Boolean(id));
    return [...new Set([...firstGroups, ...expandedVersionGroups(items, selected, Boolean(query.trim()))])];
  }, [items, selected, query]);
  useEffect(() => setExpanded(defaultExpanded), [defaultExpanded, isOpen]);
  const toggleItem: TreeViewProps<VersionTreeItem>['onItemToggle'] = ({ detail }) =>
    setExpanded((current) => (detail.expanded ? [...current, detail.id] : current.filter((id) => id !== detail.id)));
  return { expanded, toggleItem };
};

export const useVersionDropdown = (control: SelectProps, choices: VersionChoices) => {
  const text = useStarGridText();
  const disclosure = useVersionDisclosure(control.disabled);
  const dates = useVersionDateFilter();
  const { query, isOpen } = disclosure;
  const catalog = useMemo(() => buildVersionCatalog(choices.versions, query, dates.range), [choices.versions, query, dates.range]);
  const draftSnapshotCount = useMemo(
    () => (dates.editing && dates.valid ? buildVersionCatalog(choices.versions, query, dates.draft).snapshots.length : undefined),
    [choices.versions, query, dates.editing, dates.valid, dates.draft]
  );
  const preview = useVersionHistoryPreview(catalog, dates.range, query, isOpen);
  const selected = control.selectedOption?.value;
  const expansion = useVersionExpansion(catalog.snapshotGroups, selected, query, isOpen);
  const close = (): void => {
    dates.cancel();
    disclosure.close();
  };
  const dismiss = (): void => {
    dates.cancel();
    disclosure.dismiss();
  };
  const escape = (): void => {
    if (dates.editing) dates.dismiss();
    else dismiss();
  };
  const toggle = (): void => {
    dates.cancel();
    if (!disclosure.isOpen) choices.load?.();
    disclosure.toggle();
  };
  const select = (item: VersionTreeItem): void => {
    if (!item.version || item.version.resolvable === false || control.disabled) return;
    choices.select(item.version);
    dismiss();
  };
  return { text, catalog, selected, select, dates, draftSnapshotCount, preview, ...disclosure, ...expansion, close, dismiss, escape, toggle };
};
